import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { apiService } from '../services/api';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'customer' | 'employee' | 'admin' | 'bsi_admin';
  requireBSI?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRole,
  requireBSI = false,
}) => {
  const location = useLocation();
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  useEffect(() => {
    const verifyAuth = async () => {
      const token = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');

      // No token = not authenticated
      if (!token || !userStr) {
        console.log('[ProtectedRoute] No token or user found');
        setIsAuthenticated(false);
        setHasPermission(false);
        return;
      }

      try {
        const user = JSON.parse(userStr);

        console.log('[ProtectedRoute] Verifying token for user:', user.email, 'role:', user.role);

        // Verify the login is still good - through the API client, so an
        // expired access token is renewed rather than treated as signed out
        // (which is what reloading a page after 15 minutes used to do).
        const response = { data: await apiService.verifySession() };

        console.log('[ProtectedRoute] Auth verify response:', response.data);

        if (response.data.valid) {
          setIsAuthenticated(true);
          console.log('[ProtectedRoute] Token valid');

          // Check BSI admin access
          if (requireBSI) {
            // The backend decides who is a platform admin (PLATFORM_ADMIN_EMAILS).
            const isBSIAdmin = response.data.isPlatformAdmin === true;

            console.log('[ProtectedRoute] BSI admin check:', isBSIAdmin);
            setHasPermission(isBSIAdmin);
            return;
          }

          // Check role-based access
          if (requiredRole) {
            const hasRole =
              user.role === requiredRole ||
              (requiredRole === 'employee' && user.role === 'admin') ||
              (requiredRole === 'customer' && user.role === 'admin');
            console.log(
              '[ProtectedRoute] Role check - required:',
              requiredRole,
              'user:',
              user.role,
              'hasRole:',
              hasRole
            );
            setHasPermission(hasRole);
          } else {
            console.log('[ProtectedRoute] No role required, granting access');
            setHasPermission(true);
          }
        } else {
          console.log('[ProtectedRoute] Token invalid');
          setIsAuthenticated(false);
          setHasPermission(false);
        }
      } catch (error: any) {
        console.error('[ProtectedRoute] Auth verification failed:', error);
        if (error?.response?.status === 401 || requireBSI) {
          // Really signed out (the login couldn't be renewed)
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setIsAuthenticated(false);
          setHasPermission(false);
          return;
        }
        // The check itself failed (network, server restarting): don't sign
        // out over it. The server still checks the login on every request.
        try {
          const user = JSON.parse(userStr);
          setIsAuthenticated(true);
          setHasPermission(
            !requiredRole ||
              user.role === requiredRole ||
              (requiredRole === 'employee' && user.role === 'admin') ||
              (requiredRole === 'customer' && user.role === 'admin')
          );
        } catch {
          setIsAuthenticated(false);
          setHasPermission(false);
        }
      }
    };

    verifyAuth();
  }, [requiredRole, requireBSI]);

  // Still checking
  if (isAuthenticated === null || hasPermission === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    // Redirect to appropriate login page
    const loginPath = requireBSI ? '/bsi/login' : '/login';
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  // Authenticated but no permission
  if (!hasPermission) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
            <svg
              className="h-6 w-6 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
          <p className="text-sm text-gray-600 mb-6">
            You don't have permission to access this page.
            {requireBSI && ' This area is restricted to Bomizzel Services Inc. administrators.'}
          </p>
          <button
            onClick={() => window.history.back()}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Authenticated and has permission
  return <>{children}</>;
};

export default ProtectedRoute;
